/**
 * DecisionCard — the ONE canonical Decision unit (R2).
 *
 * Spec: docs/strategy/2026-07-02-cognitive-overhead-reduction.md R2 (one
 * Decision card: the five-part shape + one verb ladder).
 *
 * Every consequential Ask — a Feed staging row, a Work-item gate — renders the
 * SAME five-part shape:
 *   1. plain question        — `title` (a table row leaves it to the set-level card)
 *   2. the real artifact      — `proposal` (an editable ProposalEditor), else `description`
 *   3. literal-consequence    — `verbLabel` ("Send this reply to dana@acme.com"), never "Approve"
 *   4. always-present after-line — `effect` (one honest sentence naming what resolving does)
 *   5. one undo model         — a resolved receipt naming its `undoDeadline`
 *
 * ONE verb ladder, keyed off the backend's honest risk read — NOT the label text:
 *   - internal / reversible  (`leavesWorkspace === false`) → a calm muted verb,
 *     one-tap, an undo window on the receipt.
 *   - external / irreversible (`requiresConfirmation === true`) → the literal verb
 *     + an effect-line confirm (ConfirmationModal) before it fires.
 * The decline is always one-tap and quiet: a reject is reversible.
 *
 * Editable-proposal support (the moat): the card owns the human-edited field map
 * (seeded from each field's staged value) and hands it to `onResolve` on approve,
 * so the suggested → final revision is captured (proposal_revisions / FIT) exactly
 * like a loose action. A card with no `onResolve` (or sealed / resolved) is a
 * read-only render — no live verbs.
 *
 * Extracted from FeedArtifact's StagingRow so the Feed and Work render one unit;
 * both surface-agnostic dependencies (ProposalEditor, ConfirmationModal) are
 * reused as-is.
 */
import { useCallback, useState, type CSSProperties, type ReactNode } from "react";
import { Check } from "lucide-react";
import type { EditableProposal } from "@yagni/shared";
import { ProposalEditor } from "./proposal/ProposalEditor.js";
import { ConfirmationModal } from "./ConfirmationModal.js";

/** Approve (fire the consequence) vs reject (file it away — reversible). */
export type DecisionIntent = "approve" | "reject";

/**
 * A THIRD ending, for the rare decision that genuinely has one.
 *
 * Two verbs are the rule and stay the rule: a Decision is a thing a person
 * says yes or no to. The exception is a decision about an act that ran out of
 * its budget, where "no" is two different answers (a person finishes it, or it
 * stops), and collapsing them would make the card ask a question neither
 * button answers. It renders between the decline and the primary, quiet, and
 * it is absent on every other card.
 */
export interface DecisionAlternative {
  label: string;
  /** One sentence naming what pressing it does. */
  effect?: string;
  testId?: string;
  onSelect: () => void | Promise<void>;
}

/** The testids each part renders under, so a host keeps its own DOM contract. */
export interface DecisionCardTestIds {
  root: string;
  proposal: string;
  description: string;
  receipt: string;
  effect: string;
  declineEffect: string;
  verb: string;
  decline: string;
}

const DEFAULT_TEST_IDS: DecisionCardTestIds = {
  root: "decision-card",
  proposal: "decision-card-proposal",
  description: "decision-card-desc",
  receipt: "decision-card-receipt",
  // The shared feed effect-line testid/class — an effect line reads the same
  // everywhere it appears.
  effect: "feed-effect-line",
  declineEffect: "feed-decline-effect",
  verb: "decision-card-verb",
  decline: "decision-card-decline",
};

export interface DecisionCardProps {
  /** Part 1: the plain-language question. Omitted when a set-level card carries it. */
  title?: string;
  /** A quiet mono label above the artifact (staging: the action type; a gate: its kind). */
  eyebrow?: ReactNode;
  /** Part 2: the real, editable artifact. Null/absent → the `description` fallback. */
  proposal?: EditableProposal | null;
  /** Part 2 fallback body when there's no editable proposal (or the card is read-only). */
  description?: string;
  /** Part 3: the literal-consequence primary button copy ("Send this reply to…"). */
  verbLabel: string;
  /** Part 4: one honest sentence naming what resolving does. Sits above the verbs. */
  effect?: string;
  /**
   * The OTHER outcome, when declining does something a reader needs told rather
   * than merely filing the Decision away.
   *
   * A plan gate is the case it exists for: its decline runs a plan revision and
   * re-arms the gate, and for years the card named the request ("Request
   * changes") and never the consequence. Rendered as a second, quieter effect
   * line under the first, so a card that cannot be undone states both endings
   * before either button is pressed. Absent on every other caller, which is
   * why they are unaffected.
   */
  declineEffect?: string;
  /**
   * The honest risk read: `false` → an internal write / held draft (muted, one-tap);
   * `true`/omitted → leaves the workspace (accent affordance). Keys the verb style,
   * NOT the label text.
   */
  leavesWorkspace?: boolean;
  /** `true` → confirm before firing (external/irreversible); `false`/omitted → one-tap. */
  requiresConfirmation?: boolean;
  /**
   * Decline button copy; default "Decline". `null` renders no decline at all,
   * for an ask whose only other move is to leave it waiting.
   */
  declineLabel?: string | null;
  /** The third ending, when the decision has one. See {@link DecisionAlternative}. */
  alternative?: DecisionAlternative | null;
  /** Part 5: resolved → a receipt (no live verbs), even when a handler is present. */
  resolved?: boolean;
  /** Read-only (the whole surface is sealed) → no live verbs; receipts still show. */
  sealed?: boolean;
  /** The undo window named on the resolved receipt. */
  undoDeadline?: string | null;
  /** Override the resolved-receipt copy; defaults to the undo-window sentence. */
  receiptText?: string;
  /**
   * Resolve this Decision. `editedProposal` (approve only) carries the human-edited
   * field values so the resolve route captures the revision. Absent → a read-only
   * render (no live verbs).
   */
  onResolve?: (
    intent: DecisionIntent,
    editedProposal?: Record<string, unknown> | null,
  ) => Promise<void> | void;
  /**
   * Quiet utilities rendered at the START of the action foot (the case dock's
   * left cluster, e.g. Next item). The dock's scoped CSS pulls them left
   * and sends the verbs to the right edge; outside the dock they simply lead
   * the row. Absent everywhere that predates the dock grammar.
   */
  footStart?: ReactNode;
  /**
   * Dock mode for the after-lines (the one-line rule, 2026-08-28): the effect
   * and declineEffect ride the buttons as hover titles instead of rendering as
   * always-present lines. Pre-dock mounts keep the printed lines.
   */
  effectHover?: boolean;
  /**
   * Why this reader cannot answer, when they cannot (ADR-0044 edge case 22 and
   * the spec's "non-members see the buttons disabled with the reason").
   *
   * The verbs are RENDERED and disabled rather than hidden: a person who can
   * read the case file should be able to see exactly what decision is waiting
   * and who it is for, so they can go and ask. Hiding it would make the page
   * look like nothing is happening.
   */
  blocked?: string | null;
  /**
   * Why the PRIMARY verb alone is dead, when the other half is still live.
   *
   * A Job's act whose click has nowhere to land (ADR-0044: a ceiling is a
   * maximum, so a person can set a kind Supervised whose act YAGNI cannot
   * dispatch yet). Declining dispatches nothing, so the decline stays live and
   * only the verb goes dark, with a line naming where the act CAN be answered.
   * `blocked` outranks this: a non-member answers neither half.
   */
  verbBlocked?: string | null;
  /** DOM-id namespace for the proposal fields (many cards on one page need distinct ids). */
  fieldIdPrefix?: string;
  /** Testid overrides so a host surface keeps its existing DOM contract. */
  testIds?: Partial<DecisionCardTestIds>;
  className?: string;
  style?: CSSProperties;
}

const titleStyle: CSSProperties = {
  fontFamily: "var(--font-serif)",
  fontWeight: 600,
  fontSize: "clamp(17px, 1.7vw, 21px)",
  lineHeight: 1.2,
  color: "var(--ink)",
  margin: "0 0 2px",
};

const eyebrowStyle: CSSProperties = {
  fontFamily: "var(--mono)",
  fontSize: 10,
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  color: "var(--ink-faded)",
  marginBottom: 6,
};

const descStyle: CSSProperties = {
  fontFamily: "var(--body)",
  fontSize: 14,
  lineHeight: 1.55,
  color: "var(--ink-soft)",
  margin: 0,
};

function formatDeadline(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

/** "Approved. Ships at Jun 2, 3:00 PM, undo before then." — the receipt + undo window. */
function defaultReceiptText(undoDeadline?: string | null): string {
  return undoDeadline
    ? `Approved. Ships at ${formatDeadline(undoDeadline)}, undo before then.`
    : "Approved.";
}

/**
 * The action foot in the order the eye (and the tab key) meets it. With
 * `footStart` — the case dock's quiet utilities — the row follows the dock
 * grammar: utilities left, decline beside the primary, the PRIMARY at the
 * right edge, in DOM order, so keyboard traversal matches the visual read.
 * Without it, the classic order (primary first) is preserved for every
 * pre-dock mount (Front proposals, inbox, editions).
 */
function FootRow({
  footStart,
  verb,
  decline,
  alternative,
}: {
  footStart?: ReactNode;
  verb: ReactNode;
  decline: ReactNode;
  alternative?: ReactNode;
}) {
  return (
    <div className="feed-story-action-foot">
      {footStart != null ? (
        <>
          <div className="deccard-foot-start">{footStart}</div>
          {decline}
          {alternative}
          {verb}
        </>
      ) : (
        <>
          {verb}
          {alternative}
          {decline}
        </>
      )}
    </div>
  );
}

/** Seed the edited-proposal map from each field's staged value (or null). */
function proposalInitialValues(
  proposal: EditableProposal | null | undefined,
): Record<string, unknown> | null {
  if (!proposal) return null;
  const map: Record<string, unknown> = {};
  for (const f of proposal.fields) map[f.key] = f.value;
  return map;
}

export function DecisionCard({
  title,
  eyebrow,
  proposal,
  description,
  verbLabel,
  effect,
  declineEffect,
  leavesWorkspace,
  requiresConfirmation,
  declineLabel = "Decline",
  alternative = null,
  resolved = false,
  sealed = false,
  undoDeadline,
  receiptText,
  onResolve,
  footStart,
  effectHover = false,
  blocked = null,
  verbBlocked = null,
  fieldIdPrefix,
  testIds,
  className,
  style,
}: DecisionCardProps) {
  const t = { ...DEFAULT_TEST_IDS, ...testIds };

  const [values, setValues] = useState<Record<string, unknown> | null>(() =>
    proposalInitialValues(proposal),
  );
  const [confirming, setConfirming] = useState(false);
  const [pending, setPending] = useState(false);

  const canAct = !!onResolve && !sealed && !resolved;
  const editable = canAct && !!proposal;

  const onFieldChange = useCallback(
    (key: string, value: unknown) => {
      setValues((prev) => ({
        ...(prev ?? proposalInitialValues(proposal) ?? {}),
        [key]: value,
      }));
    },
    [proposal],
  );

  const fire = useCallback(
    async (intent: DecisionIntent) => {
      if (pending || !onResolve) return;
      setPending(true);
      try {
        // Carry the edited values into an approve (never a decline).
        await onResolve(intent, intent === "approve" ? values : undefined);
      } finally {
        setPending(false);
      }
    },
    [onResolve, pending, values],
  );

  // The muted internal affordance keys off the backend's honest risk read
  // (leavesWorkspace === false ⇔ internal write or held draft-file), never the
  // label text — a recipient like ops@tracking.io must not mute a real send's
  // button — and never requiresConfirmation, since a reversible native send is
  // one-tap yet still external.
  const isTrack = leavesWorkspace === false;
  const verbClass = isTrack ? "feed-act-track" : "feed-act-approve";

  return (
    <div data-testid={t.root} className={className} style={style}>
      {eyebrow != null && eyebrow !== "" && <div style={eyebrowStyle}>{eyebrow}</div>}
      {title && <h2 style={titleStyle}>{title}</h2>}

      {/* Part 2 — the real artifact: its editable proposal (edit before send),
          else its description. */}
      {editable && proposal ? (
        <div data-testid={t.proposal}>
          <ProposalEditor
            proposal={proposal}
            values={values}
            onChange={onFieldChange}
            idPrefix={fieldIdPrefix}
          />
        </div>
      ) : (
        <p data-testid={t.description} style={descStyle}>
          {description}
        </p>
      )}

      {resolved ? (
        <div className="feed-action-receipt" data-testid={t.receipt} role="status">
          <span className="feed-action-receipt-ic" aria-hidden>
            <Check width={12} height={12} strokeWidth={2.75} />
          </span>
          <span>{receiptText ?? defaultReceiptText(undoDeadline)}</span>
        </div>
      ) : canAct ? (
        <>
          {/* Why the verbs are dead for this reader. Printed whatever the dock
              mode says: a disabled button with no reason is a dead end. */}
          {blocked && (
            <p className="feed-effect-line is-decline" data-testid="deccard-blocked">
              {blocked}
            </p>
          )}
          {/* Why the primary alone is dead, and where the act can be answered. */}
          {!blocked && verbBlocked && (
            <p className="feed-effect-line is-decline" data-testid="deccard-verb-blocked">
              {verbBlocked}
            </p>
          )}
          {/* Part 4 — the after-line. In dock mode (`effectHover`) it rides the
              buttons as hover titles instead (the one-line rule). */}
          {effect && !effectHover && (
            <p className="feed-effect-line" data-testid={t.effect}>
              {effect}
            </p>
          )}
          {/* The other ending, when declining is an act rather than a filing. */}
          {declineEffect && !effectHover && (
            <p className="feed-effect-line is-decline" data-testid={t.declineEffect}>
              {declineEffect}
            </p>
          )}
          {/* The third ending's own after-line, under the other two, so a
              card with three endings still states each one before any button
              is pressed. */}
          {alternative?.effect && !effectHover && (
            <p className="feed-effect-line is-decline" data-testid="deccard-alternative-effect">
              {alternative.effect}
            </p>
          )}
          <FootRow
            footStart={footStart}
            alternative={
              alternative ? (
                <button
                  type="button"
                  className="feed-act-decline"
                  data-testid={alternative.testId ?? "decision-card-alternative"}
                  data-verb="alternative"
                  disabled={pending || blocked != null}
                  {...(blocked
                    ? { title: blocked }
                    : effectHover && alternative.effect
                      ? { title: alternative.effect }
                      : {})}
                  onClick={() => {
                    if (pending) return;
                    void alternative.onSelect();
                  }}
                >
                  {alternative.label}
                </button>
              ) : null
            }
            verb={
              /* Part 3 — the literal-consequence primary button (one verb ladder). */
              <button
                type="button"
                className={verbClass}
                data-testid={t.verb}
                data-verb={isTrack ? "track" : "approve"}
                disabled={pending || blocked != null || verbBlocked != null}
                {...(blocked ?? verbBlocked
                  ? { title: (blocked ?? verbBlocked)! }
                  : effectHover && effect
                    ? { title: effect }
                    : {})}
                onClick={() => {
                  // External / irreversible → confirm; internal → one-tap fire.
                  if (requiresConfirmation) setConfirming(true);
                  else void fire("approve");
                }}
              >
                {verbLabel}
              </button>
            }
            decline={
              /* The decline: a reject is reversible (it can resurface), so no
                 confirm — it just files this one Decision away. */
              declineLabel === null ? null : (
              <button
                type="button"
                className="feed-act-decline"
                data-testid={t.decline}
                data-verb="reject"
                disabled={pending || blocked != null}
                {...(blocked
                  ? { title: blocked }
                  : effectHover && declineEffect
                    ? { title: declineEffect }
                    : {})}
                onClick={() => void fire("reject")}
              >
                {declineLabel}
              </button>
              )
            }
          />
        </>
      ) : null}

      <ConfirmationModal
        open={confirming}
        caption={verbLabel}
        title={`${verbLabel}?`}
        body={effect ?? verbLabel}
        confirmLabel={verbLabel}
        pending={pending}
        onConfirm={() => {
          setConfirming(false);
          void fire("approve");
        }}
        onCancel={() => setConfirming(false)}
      />
    </div>
  );
}
